import { authClient } from './baseAPI';
import { config } from '@/config';
import { normalizeAlert } from '@/lib/alertNormalizer';
import type { AlertEvent } from '@/types/event';
import type { ZoneMaxEntry } from '@/types/alertWindow';

export interface EventsQuery {
  device_id?: string;
  camera_id?: string;
  since?: string;
  limit?: number;
}

// Envelope shape the backend uses for both /events/alerts-list and the
// derived list call. Kept local — the consumer normalizes each item into
// the FE's AlertEvent shape via useDashboardAlerts.normalizeAlert, so we
// purposely don't over-narrow the row type here.
interface AlertsListEnvelope {
  data?: { items?: unknown[]; total?: number };
  success?: boolean;
  error?: string;
}

/** Detail shape returned by GET /events/by-id/{event_id}. Powers the
 *  Live Alerts evidence drawer. Most fields are optional because alerts
 *  are inserted at open and enriched as evidence lands — the drawer
 *  polls until `clip_play_url` shows up (or the timeout elapses). */
export interface EventDetail {
  event_id: string;
  event_type?: string;
  schema_version?: number;
  camera_id?: string;       // stream_name as stored in ai_events
  camera_db_id?: string;    // dashboard camera _id (for /devices deep-link)
  stream_name?: string;
  camera_name?: string;
  ai_type?: string;
  model?: string;
  first_zone_label?: string;
  polygon_label?: string;
  person_count?: number;
  max_confidence?: number;
  class_counts?: Record<string, { max_total?: number; max_in_zone?: number; max_conf?: number }>;
  timestamp?: string;
  window_opened_at?: string;
  window_closes_at?: string;
  window_closed?: boolean;
  clip_object_key?: string;
  clip_play_url?: string;
  clip_play_url_expires_at?: string;
  clip_uploaded_at?: string;
  clip_size_bytes?: number;
  // Capture-dispatch forensics — set by ai_worker's capture_dispatcher when
  // it publishes the agent_cmd. Lets the cloud UI distinguish between
  // "never asked the agent" (no field) vs. "asked but the agent never
  // responded" (capture_dispatched_at set but clip_object_key missing).
  capture_dispatched_at?: string;
  // 0 = no subscribers received the publish (grpc_ingestion is probably
  // down). 1+ = at least one ingestion replica got it. Drives the FE's
  // retry hint copy.
  capture_subs?: number;
  capture_dispatch_ok?: boolean;
  capture_command_id?: string;
  capture_recapture_count?: number;
  telegram_status?: string;

  // Persistent thumbnail (cloud-side capture at alert OPEN). Mirrors the
  // alerts-list payload — `thumbnail_url` is minted fresh by the API per
  // request from `thumbnail_object_key` (see [[minio-presigned-url-at-read]]).
  thumbnail_object_key?: string;
  thumbnail_url?: string;
  thumbnail_url_expires_at?: string;

  // Schema-v3 per-zone fan-out. Each entry carries the per-class running
  // max for the zone plus AI-type-specific extensions:
  //   - face_detections (face)        — recognised + unknown identities
  //   - ppe_missing_items (ppe)       — items missing on at least one violator
  //   - ppe_violator_count (ppe)      — max distinct violators in the window
  // The drawer's "Detection details" section renders the relevant block.
  zones_max_by_id?: Record<string, ZoneMaxEntry>;

  // Window forensics — duration, frame count, last activity. Drives the
  // drawer's Timeline sub-section.
  window_sec?: number;
  frame_count?: number;
  final_frame_count?: number;
  last_seen_at?: string;

  // Sparse model-specific metadata persisted at OPEN. Currently:
  //   - entry_exit  (humanCounter) — occupancy snapshot + today's counts
  // Whatever the firing model stamped rides through; the drawer's
  // branching renderer decides what to surface.
  metadata?: {
    entry_exit?: {
      occupancy: number;
      entry_count_today: number;
      exit_count_today: number;
      peak_today: number;
    };
    [k: string]: unknown;
  };
}

interface RecaptureResponse {
  success: boolean;
  subscribers?: number;
  command_id?: string;
  already_captured?: boolean;
  error?: string;
}

interface EventDetailEnvelope {
  data: EventDetail | null;
  success: boolean;
  error?: string;
}

// Shape returned by /events/alert-counts. The endpoint already aggregates
// per-camera so the FE doesn't need to pull 500 row docs just to call
// `.length` — kept narrow to the one field we read here.
interface AlertCountsEnvelope {
  data?: Record<string, { count?: number }>;
}

export const eventsAPI = {
  // The backend has no GET /events root — the actual historical-alerts
  // endpoint is /events/alerts-list (schema-v3 alert windows, scoped to
  // the caller). The previous implementation hit /api/events which 404'd
  // silently in every consumer's `.catch(() => [])`, so the dashboard's
  // seed never landed (live feed + chart appeared dead until a WS alert
  // arrived) and the KPI "alerts today" was permanently 0. Translate the
  // legacy `since` shape to `start` and unwrap the {data:{items}} envelope
  // here so callers stay shape-agnostic.
  list: async (params: EventsQuery = {}): Promise<AlertEvent[]> => {
    const qp: Record<string, string | number> = {};
    if (params.since) qp.start = params.since;
    if (params.camera_id) qp.camera_id = params.camera_id;
    if (params.device_id) qp.device_id = params.device_id;
    qp.limit = params.limit ?? 50;
    const { data } = await authClient.get<AlertsListEnvelope>(
      `${config.api.events}/alerts-list`,
      { params: qp },
    );
    if (!data?.success) return [];
    // Normalize at the API boundary so every consumer (dashboard seed,
    // KPI counter, device-detail recent list, etc.) sees the same
    // canonical AlertEvent shape regardless of which raw payload arrived.
    const rows = data.data?.items ?? [];
    const out: AlertEvent[] = [];
    for (const r of rows) {
      const n = normalizeAlert(r);
      if (n) out.push(n);
    }
    return out;
  },

  /** Lightweight aggregate count of alert windows in [start, end).
   *  Hits /events/alert-counts which returns `{<camera_id>: {count}}` per
   *  the caller's cameras — we just sum the counts. Used by the dashboard's
   *  "Alerts today" KPI: pulling 500 row docs to call `.length` was the
   *  previous approach and (a) hit the backend's 500-row cap (so days with
   *  more activity silently undercounted) and (b) wasted bandwidth +
   *  Mongo work to throw the docs away. */
  countSince: async (sinceISO: string): Promise<number> => {
    const { data } = await authClient.get<AlertCountsEnvelope>(
      `${config.api.events}/alert-counts`,
      { params: { start: sinceISO } },
    );
    const rows = data?.data ?? {};
    let total = 0;
    for (const v of Object.values(rows)) {
      total += Number(v?.count ?? 0);
    }
    return total;
  },

  /** Per-camera count map, same `/events/alert-counts` endpoint as
   *  countSince but returns the un-summed map. Powers the per-tile
   *  "N alerts today" chip on the Cameras grid — fetching once at the
   *  page level lets every tile read its count from a single shared map
   *  without N+1 fetches. */
  countsByCamera: async (sinceISO?: string): Promise<Record<string, number>> => {
    const params: Record<string, string> = {};
    if (sinceISO) params.start = sinceISO;
    const { data } = await authClient.get<AlertCountsEnvelope>(
      `${config.api.events}/alert-counts`,
      { params },
    );
    const rows = data?.data ?? {};
    const out: Record<string, number> = {};
    for (const [cameraId, v] of Object.entries(rows)) {
      out[cameraId] = Number(v?.count ?? 0);
    }
    return out;
  },

  /** Fetch a single alert-window doc by its `event_id` (UUID). Returns
   *  null when the event doesn't exist OR doesn't belong to the caller.
   *  Backend uses 404-as-empty for both cases to avoid leaking tenant
   *  boundaries through error codes — so a `null` return is "show empty
   *  state", never an auth-failure toast. */
  getById: async (eventId: string): Promise<EventDetail | null> => {
    const { data } = await authClient.get<EventDetailEnvelope>(
      `${config.api.events}/by-id/${encodeURIComponent(eventId)}`,
    );
    return data?.success ? data.data : null;
  },

  /** Per-hour, per-ai-type alert-window counts for the dashboard timeline
   *  chart. Backend aggregates schema-v3 ai_events into 24 buckets per
   *  ai_type so the chart reflects ACTUAL consolidated windows (the truth
   *  in Mongo) instead of the inflated MQTT firehose that the live store
   *  carries (~9× over-count per window — see ai_event_pipeline memory).
   *
   *  Hours with zero counts are omitted server-side; caller fills with 0
   *  when bucketing 24 hours of axis labels. */
  hourlyBuckets: async (
    sinceISO?: string,
    tzOffsetMinutes?: number,
  ): Promise<Record<string, Record<number, number>>> => {
    const params: Record<string, string | number> = {};
    if (sinceISO) params.start = sinceISO;
    if (typeof tzOffsetMinutes === 'number') params.tz_offset_minutes = tzOffsetMinutes;
    const { data } = await authClient.get<{ buckets?: Record<string, Record<string, number>> }>(
      `${config.api.events}/hourly-buckets`,
      { params },
    );
    const raw = data?.buckets ?? {};
    const out: Record<string, Record<number, number>> = {};
    for (const [aiType, hours] of Object.entries(raw)) {
      const numKeyed: Record<number, number> = {};
      for (const [h, n] of Object.entries(hours)) {
        numKeyed[Number(h)] = Number(n);
      }
      out[aiType] = numKeyed;
    }
    return out;
  },

  /** Manually re-dispatch capture_evidence for a stuck event. Use when
   *  the original capture got lost (Redis blip, agent disconnected at
   *  the time, grpc_ingestion replica down). Idempotent on the backend —
   *  if a clip is already attached, returns `already_captured: true`
   *  without firing a new dispatch.
   *
   *  Returns `subscribers: 0` when no ingestion replica received the
   *  publish — the FE should surface this as "ingestion offline" so
   *  the operator knows a retry won't help until the cloud comes back. */
  recapture: async (eventId: string): Promise<RecaptureResponse> => {
    const { data } = await authClient.post<RecaptureResponse>(
      `${config.api.events}/by-id/${encodeURIComponent(eventId)}/recapture`,
    );
    return data;
  },
};
